<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="720" persistent>
    <v-card rounded="xl">
      <!-- Header -->
      <v-card-title
        class="text-h6 font-weight-bold pt-5 px-6 d-flex align-center justify-space-between"
      >
        <span>{{ headerTitle }}</span>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <!-- ── DETAIL VIEW (plan already exists) ── -->
      <template v-if="isDetailMode">
        <v-card-text class="px-6 pt-5 pb-2">
          <!-- Loading -->
          <div v-if="loadingPlan" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate :color="colors.primaryVariant" />
          </div>

          <template v-else-if="planDetail">
            <!-- Summary chips -->
            <div class="d-flex flex-wrap mb-4" style="gap: 8px">
              <v-chip small outlined :style="{ borderColor: colors.primaryVariant, color: colors.primaryVariant }">
                {{ planDetail.installmentsCount }} pagos
              </v-chip>
              <v-chip small outlined :style="{ borderColor: colors.primaryVariant, color: colors.primaryVariant }">
                Cada {{ planDetail.intervalMonths }} {{ planDetail.intervalMonths === 1 ? 'mes' : 'meses' }}
              </v-chip>
              <v-chip small outlined :style="{ borderColor: colors.primaryVariant, color: colors.primaryVariant }">
                {{ formatCurrency(planDetail.amountPerInstallment) }} / cuota
              </v-chip>
              <v-chip
                small
                class="font-weight-bold white--text"
                :style="{ backgroundColor: planStatusColor(planDetail.status) }"
              >
                {{ planStatusLabel(planDetail.status) }}
              </v-chip>
            </div>

            <!-- Progress bar -->
            <div class="mb-4">
              <div class="d-flex justify-space-between text-caption mb-1">
                <span>Avance del plan</span>
                <span :style="{ color: colors.primaryVariant }" class="font-weight-bold">
                  {{ planDetail.paidInstallments }} de {{ planDetail.installmentsCount }} pagados
                </span>
              </div>
              <v-progress-linear
                :value="progressPercent"
                rounded
                height="8"
                :color="colors.primaryVariant"
                background-color="grey lighten-3"
              />
            </div>

            <!-- Installments table -->
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">#</th>
                    <th class="text-left">Monto</th>
                    <th class="text-left">Fecha límite</th>
                    <th class="text-left">Estado</th>
                    <th class="text-left">Pagado el</th>
                    <th class="text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="inst in planDetail.installments" :key="inst._id">
                    <td>{{ inst.number }}</td>
                    <td>{{ formatCurrency(inst.amount) }}</td>
                    <td>{{ formatDate(inst.dueDate) }}</td>
                    <td>
                      <v-chip
                        x-small
                        class="font-weight-bold white--text"
                        :style="{ backgroundColor: installmentStatusColor(inst.status) }"
                      >
                        {{ installmentStatusLabel(inst.status) }}
                      </v-chip>
                    </td>
                    <td class="text-caption grey--text">{{ inst.paidAt ? formatDate(inst.paidAt) : '—' }}</td>
                    <td>
                      <v-btn
                        v-if="isNextPendingInstallment(inst)"
                        x-small
                        rounded
                        class="font-weight-bold white--text text-none"
                        :style="{ backgroundColor: colors.primaryVariant }"
                        :loading="completingInstallmentId === inst._id"
                        :disabled="!!completingInstallmentId"
                        @click="confirmCompleteInstallment(inst)"
                      >
                        Completar pago
                      </v-btn>
                      <span v-else-if="inst.status === 'PAID'" class="text-caption grey--text">—</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <!-- Confirm complete dialog -->
            <v-dialog v-model="confirmCompleteDialog" max-width="400" persistent>
              <v-card rounded="xl">
                <v-card-title class="text-subtitle-1 font-weight-bold pt-5 px-6">
                  Confirmar completar pago
                </v-card-title>
                <v-card-text class="px-6 pb-2">
                  <p>
                    ¿Estás seguro de marcar la cuota
                    <strong>#{{ installmentToComplete && installmentToComplete.number }}</strong> por
                    <strong>{{ formatCurrency(installmentToComplete && installmentToComplete.amount) }}</strong>
                    como pagada?
                  </p>
                  <p class="text-caption grey--text mt-2">
                    Esta acción liberará esta cuota y permitirá al miembro avanzar al siguiente pago parcial.
                  </p>
                </v-card-text>
                <v-card-actions class="pa-4">
                  <v-spacer />
                  <v-btn text rounded color="grey darken-1" :disabled="!!completingInstallmentId" @click="confirmCompleteDialog = false">
                    Cancelar
                  </v-btn>
                  <v-btn
                    rounded
                    class="font-weight-bold white--text text-none"
                    :style="{ backgroundColor: colors.primaryVariant }"
                    :loading="!!completingInstallmentId"
                    @click="executeCompleteInstallment"
                  >
                    Sí, completar pago
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>

          <v-alert v-else-if="planError" type="error" dense class="rounded-lg mt-3">
            {{ planError }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text rounded color="grey darken-1" @click="close">Cerrar</v-btn>
        </v-card-actions>
      </template>

      <!-- ── CREATE VIEW ── -->
      <template v-else>
      <v-card-text v-if="!confirmScreen" class="px-6 pt-5 pb-2">
        <!-- Switch -->
        <div class="d-flex align-center mb-4">
          <v-switch
            v-model="enablePlan"
            inset
            hide-details
            class="ma-0 pa-0 mr-3"
            :color="colors.primaryVariant"
          />
          <span class="font-weight-medium">Activar pagos parciales</span>
        </div>

        <template v-if="enablePlan">
          <!-- Número de pagos -->
          <v-select
            v-model="installmentsCount"
            :items="installmentsOptions"
            label="Número de pagos parciales del plan"
            outlined
            dense
            rounded
            class="mb-3"
          />

          <!-- Intervalo entre pagos -->
          <v-select
            v-model="intervalMonths"
            :items="intervalOptions"
            label="Intervalo entre pagos parciales"
            outlined
            dense
            rounded
            class="mb-3"
          />

          <!-- Fecha límite primer pago -->
          <v-menu
            v-model="datePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="firstPaymentDeadlineFormatted"
                label="Fecha límite del primer pago"
                prepend-inner-icon="mdi-calendar"
                outlined
                dense
                rounded
                readonly
                v-bind="attrs"
                v-on="on"
                class="mb-1"
              />
            </template>
            <v-date-picker
              v-model="firstPaymentDeadline"
              :min="minDate"
              @input="datePicker = false"
              locale="es"
            />
          </v-menu>

          <!-- Info warning -->
          <v-alert
            outlined
            type="warning"
            dense
            class="rounded-lg mb-3 text-caption"
            icon="mdi-information-outline"
            color="orange darken-1"
          >
            La fecha límite del primer pago parcial determinará las fechas límites de cada pago
            parcial del plan configurado.
          </v-alert>

          <!-- Aviso solo para eventos -->
          <template v-if="eventId">
            <v-alert
              outlined
              type="info"
              dense
              class="rounded-lg mb-3 text-caption"
              icon="mdi-information-outline"
              :color="colors.primaryVariant"
            >
              La configuración del plan de pagos nunca debe exceder la fecha de comienzo del
              evento, de manera que el último pago del plan deberá programarse para al menos un
              día antes de la fecha del evento. Solamente al liquidar el último pago del plan, la
              plataforma enviará el acceso con QR para asistir al evento.
            </v-alert>

            <!-- Error si excede la fecha del evento -->
            <v-alert
              v-if="exceedsEventDate"
              type="error"
              dense
              class="rounded-lg mb-3 text-caption"
            >
              Lo sentimos, la configuración del plan de pagos excede la fecha de comienzo del
              evento. Por favor ajusta el número de pagos, el intervalo o la fecha del primer
              pago.
            </v-alert>
          </template>
        </template>
      </v-card-text>

      <!-- Screen 2: Confirmation -->
      <v-card-text v-else class="px-6 pt-5 pb-2">
        <p>
          Estás configurando un plan de
          <strong>{{ installmentsCount }} pagos parciales</strong> con intervalos de
          <strong>{{ intervalMonths }} {{ intervalMonths === 1 ? 'mes' : 'meses' }}</strong>
          entre cada pago para
          <strong>{{ orderName }}</strong> comenzando pagos el
          <strong>{{ firstPaymentDeadlineFormatted }}</strong>.
        </p>
        <p class="mt-3">
          Al avanzar y guardar este plan, el miembro será notificado en automático vía correo
          electrónico. ¿Deseas continuar?
        </p>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <template v-if="!confirmScreen">
          <v-btn text rounded color="grey darken-1" @click="close">Cancelar</v-btn>
          <v-btn
            rounded
            class="font-weight-bold white--text"
            :style="{ backgroundColor: colors.primaryVariant }"
            :disabled="!canSave"
            @click="goToConfirm"
          >
            Guardar
          </v-btn>
        </template>
        <template v-else>
          <v-btn text rounded color="grey darken-1" :disabled="saving" @click="confirmScreen = false">
            Cancelar
          </v-btn>
          <v-btn
            rounded
            class="font-weight-bold white--text"
            :style="{ backgroundColor: colors.primaryVariant }"
            :loading="saving"
            @click="savePlan"
          >
            Sí, guardar y notificar al miembro
          </v-btn>
        </template>
      </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { colors } from '@/constants/colors.js';

export default {
  name: 'PartialPaymentDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Object,
      default: null,
    },
    affiliationId: {
      type: String,
      default: null,
    },
    eventId: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    colors,
    // create mode
    confirmScreen: false,
    saving: false,
    enablePlan: false,
    installmentsCount: 6,
    intervalMonths: 1,
    firstPaymentDeadline: null,
    datePicker: false,
    // detail mode
    loadingPlan: false,
    planDetail: null,
    planError: null,
    // complete installment
    confirmCompleteDialog: false,
    installmentToComplete: null,
    completingInstallmentId: null,
  }),
  computed: {
    isDetailMode() {
      return !!(this.order && this.order.paymentPlan);
    },
    headerTitle() {
      if (this.isDetailMode) return 'Detalle del Plan de Pagos';
      if (this.confirmScreen) return 'Confirmar Plan de Pagos';
      return 'Pagos Parciales';
    },
    progressPercent() {
      if (!this.planDetail) return 0;
      return Math.round((this.planDetail.paidInstallments / this.planDetail.installmentsCount) * 100);
    },
    installmentsOptions() {
      return Array.from({ length: 11 }, (_, i) => ({ text: `${i + 2} pagos`, value: i + 2 }));
    },
    intervalOptions() {
      return [
        { text: '1 mes', value: 1 },
        { text: '2 meses', value: 2 },
        { text: '3 meses', value: 3 },
        { text: '4 meses', value: 4 },
      ];
    },
    minDate() {
      return new Date().toISOString().substring(0, 10);
    },
    firstPaymentDeadlineFormatted() {
      if (!this.firstPaymentDeadline) return '';
      const [year, month, day] = this.firstPaymentDeadline.split('-');
      return `${day}/${month}/${year}`;
    },
    lastInstallmentDate() {
      if (!this.firstPaymentDeadline) return null;
      const base = new Date(this.firstPaymentDeadline + 'T00:00:00');
      base.setMonth(base.getMonth() + (this.installmentsCount - 1) * this.intervalMonths);
      return base;
    },
    exceedsEventDate() {
      if (!this.eventId || !this.lastInstallmentDate) return false;
      const eventStart = this.order?.eventStartDate ? new Date(this.order.eventStartDate) : null;
      if (!eventStart) return false;
      // Last installment must be at least 1 day before event start
      const oneDayBefore = new Date(eventStart);
      oneDayBefore.setDate(oneDayBefore.getDate() - 1);
      return this.lastInstallmentDate > oneDayBefore;
    },
    canSave() {
      return (
        this.enablePlan &&
        this.installmentsCount >= 2 &&
        this.intervalMonths >= 1 &&
        !!this.firstPaymentDeadline &&
        !this.exceedsEventDate
      );
    },
    orderName() {
      if (!this.order) return '';
      return this.order.nameFormatted || this.order.name || '';
    },
  },
  watch: {
    value(val) {
      if (val) {
        if (this.isDetailMode) this.fetchPlanDetail();
      } else {
        this.reset();
      }
    },
  },
  methods: {
    reset() {
      this.confirmScreen = false;
      this.saving = false;
      this.enablePlan = false;
      this.installmentsCount = 6;
      this.intervalMonths = 1;
      this.firstPaymentDeadline = null;
      this.planDetail = null;
      this.planError = null;
      this.loadingPlan = false;
      this.confirmCompleteDialog = false;
      this.installmentToComplete = null;
      this.completingInstallmentId = null;
    },
    close() {
      this.$emit('input', false);
    },
    async fetchPlanDetail() {
      const planId =
        typeof this.order.paymentPlan === 'object'
          ? this.order.paymentPlan._id
          : this.order.paymentPlan;
      if (!planId) return;
      this.loadingPlan = true;
      this.planError = null;
      try {
        const { data } = await this.axios.get(`/payment-plans/${planId}`);
        this.planDetail = data.paymentPlan;
      } catch (e) {
        this.planError =
          e?.response?.data?.error?.message || 'Error al cargar el plan de pagos.';
      } finally {
        this.loadingPlan = false;
      }
    },
    goToConfirm() {
      if (!this.canSave) return;
      this.confirmScreen = true;
    },
    async savePlan() {
      if (!this.order) return;
      this.saving = true;
      try {
        await this.axios({
          method: 'POST',
          url: `/payment-plans/${this.order.id}`,
          data: {
            installmentsCount: this.installmentsCount,
            intervalMonths: this.intervalMonths,
            firstPaymentDeadline: this.firstPaymentDeadline,
          },
        });
        this.$emit('saved');
        this.$emit('input', false);
      } catch (error) {
        const msg =
          error?.response?.data?.error?.message ||
          'Ha ocurrido un error al crear el plan de pagos.';
        // Emit error so parent can show notification
        this.$emit('error', msg);
        this.confirmScreen = false;
      } finally {
        this.saving = false;
      }
    },
    isNextPendingInstallment(inst) {
      if (!this.planDetail || inst.status === 'PAID') return false;
      const pending = this.planDetail.installments
        .filter((i) => i.status === 'PENDING' || i.status === 'OVERDUE')
        .sort((a, b) => a.number - b.number);
      return pending.length > 0 && pending[0]._id === inst._id;
    },
    confirmCompleteInstallment(inst) {
      this.installmentToComplete = inst;
      this.confirmCompleteDialog = true;
    },
    async executeCompleteInstallment() {
      if (!this.installmentToComplete || !this.planDetail) return;
      const instId = this.installmentToComplete._id;
      this.completingInstallmentId = instId;
      try {
        await this.axios.put(
          `/payment-plans/${this.planDetail._id}/installments/${instId}/complete`
        );
        this.confirmCompleteDialog = false;
        this.installmentToComplete = null;
        // Refresh plan detail
        await this.fetchPlanDetail();
        this.$emit('saved');
      } catch (e) {
        const msg =
          e?.response?.data?.error?.message || 'Error al completar la cuota.';
        this.$emit('error', msg);
      } finally {
        this.completingInstallmentId = null;
      }
    },
    formatCurrency(amount) {
      if (amount == null) return '—';
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    planStatusColor(status) {
      return { ACTIVE: '#1b273e', COMPLETED: '#4caf50', CANCELLED: '#f44336' }[status] || '#9e9e9e';
    },
    planStatusLabel(status) {
      return { ACTIVE: 'Activo', COMPLETED: 'Completado', CANCELLED: 'Cancelado' }[status] || status;
    },
    installmentStatusColor(status) {
      return { PENDING: '#9e9e9e', PAID: '#4caf50', OVERDUE: '#f44336' }[status] || '#9e9e9e';
    },
    installmentStatusLabel(status) {
      return { PENDING: 'Pendiente', PAID: 'Pagado', OVERDUE: 'Vencido' }[status] || status;
    },
  },
};
</script>
